import { Component, Input, OnChanges } from '@angular/core';
import { deleteDialog } from 'src/app/shared/dialog';
import {
  Agent,
  AGENT_PERMISSIONS,
  Channel,
  CHANNEL_MODES,
  ChannelMode,
  ChannelRequest,
} from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';
import { copyToClipboard } from '../../utils/clipboard';

interface ChannelForm {
  allowedOrigins: string[];
  mode: ChannelMode;
  title: string;
  primaryColor: string;
  position: string;
  launcherLabel: string;
  enabled: boolean;
}

export const WIDGET_POSITIONS = ['bottom-right', 'bottom-left'];

/** True for "*" or scheme://host[:port] with no path (same rule as the service's OriginPolicy). */
export function isValidOrigin(value: string): boolean {
  if (value === '*') return true;
  return /^https?:\/\/[a-z0-9.-]+(:\d{1,5})?$/i.test(value);
}

@Component({
  selector: 'app-ai-agent-channels',
  templateUrl: './agent-channels.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class AgentChannelsComponent implements OnChanges {
  @Input() agent!: Agent;

  readonly P = AGENT_PERMISSIONS;
  readonly modes = CHANNEL_MODES;
  readonly positions = WIDGET_POSITIONS;

  channels: Channel[] = [];
  loading = false;

  editorOpen = false;
  editingId: string | null = null;
  form: ChannelForm = this.blankForm();
  formError = '';

  /** The channel whose key and snippet are shown after a save. */
  justSaved: Channel | null = null;

  constructor(private api: AiAgentsApiService, public perms: AgentPermissionsService) {}

  ngOnChanges(): void {
    if (this.agent) {
      this.load();
    }
  }

  load(): void {
    this.loading = true;
    this.api.listChannels(this.agent.id).subscribe({
      next: (list) => {
        this.channels = list || [];
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  openCreate(): void {
    this.editingId = null;
    this.form = this.blankForm();
    this.form.title = this.agent.name || '';
    this.formError = '';
    this.editorOpen = true;
  }

  openEdit(c: Channel): void {
    const s = c.settings || {};
    this.editingId = c.id;
    this.form = {
      allowedOrigins: [...(c.allowedOrigins || [])],
      mode: c.mode || 'AI',
      title: str(s.title),
      primaryColor: str(s.primaryColor) || '#652c8f',
      position: str(s.position) || 'bottom-right',
      launcherLabel: str(s.launcherLabel),
      enabled: c.enabled,
    };
    this.formError = '';
    this.editorOpen = true;
  }

  closeEditor(): void {
    this.editorOpen = false;
  }

  originsChanged(values: string[]): void {
    // Normalise: trim, drop a trailing slash, no duplicates.
    const seen = new Set<string>();
    this.form.allowedOrigins = (values || [])
      .map((v) => v.trim().replace(/\/+$/, ''))
      .filter((v) => v && !seen.has(v.toLowerCase()) && seen.add(v.toLowerCase()));
  }

  get invalidOrigins(): string[] {
    return this.form.allowedOrigins.filter((o) => !isValidOrigin(o));
  }

  save(): void {
    if (this.form.allowedOrigins.length === 0) {
      this.formError = 'Add at least one allowed origin, e.g. https://www.example.com';
      return;
    }
    if (this.invalidOrigins.length) {
      this.formError = `Not a valid origin: ${this.invalidOrigins.join(', ')}. Use scheme://host[:port] or *.`;
      return;
    }
    const settings: Record<string, unknown> = {};
    const put = (k: string, v: string) => {
      if (v && v.trim()) settings[k] = v.trim();
    };
    put('title', this.form.title);
    put('primaryColor', this.form.primaryColor);
    put('position', this.form.position);
    put('launcherLabel', this.form.launcherLabel);

    const body: ChannelRequest = {
      type: 'WEB_WIDGET',
      allowedOrigins: this.form.allowedOrigins,
      mode: this.form.mode,
      settings,
      enabled: this.form.enabled,
    };
    const call = this.editingId
      ? this.api.updateChannel(this.agent.id, this.editingId, body)
      : this.api.createChannel(this.agent.id, body);
    call.subscribe({
      next: (saved) => {
        this.editorOpen = false;
        this.justSaved = saved;
        this.load();
      },
      error: (err) => (this.formError = err?.error?.message || 'Saving the channel failed.'),
    });
  }

  toggleEnabled(c: Channel): void {
    const body: ChannelRequest = {
      type: c.type,
      allowedOrigins: c.allowedOrigins,
      mode: c.mode,
      settings: c.settings || {},
      enabled: !c.enabled,
    };
    this.api.updateChannel(this.agent.id, c.id, body).subscribe({ next: () => this.load() });
  }

  async remove(c: Channel): Promise<void> {
    const confirm = await deleteDialog();
    if (!confirm.isConfirmed) {
      return;
    }
    this.api.deleteChannel(this.agent.id, c.id).subscribe({
      next: () => {
        if (this.justSaved?.id === c.id) this.justSaved = null;
        this.load();
      },
    });
  }

  copy(text: string, label: string): void {
    copyToClipboard(text, label);
  }

  private blankForm(): ChannelForm {
    return {
      allowedOrigins: [],
      mode: 'AI',
      title: '',
      primaryColor: '#652c8f',
      position: 'bottom-right',
      launcherLabel: '',
      enabled: true,
    };
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v);
}
